import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  Action,
  Select,
  Selector,
  State,
  StateContext,
  Store,
} from "@ngxs/store";
import { MemberCorrespondenceTypeActions } from "app/addresses/store/member-correspondence-type";
import { MemberProfessionalDomicileActions } from "app/addresses/store/member-professional-domicile";
import { MemberResidenceActions } from "app/addresses/store/member-residence";
import { MemberSecondaryWorkAddressActions } from "app/addresses/store/member-secondary-work-address";
import { AuthUser } from "app/core/auth/core/auth-user.model";
import { AuthState } from "app/core/auth/core/auth.state";
import { UserMemberService } from "app/services/user-member.service";
import { Observable, Subject } from "rxjs";
import {
  catchError,
  map,
  switchMap,
  take,
  takeUntil,
  tap
} from "rxjs/operators";
import { MemberCorrespondenceType } from "~app/addresses/store/member-correspondence-type/member-correspondence-type.model";
import { MemberCorrespondenceTypeState } from "~app/addresses/store/member-correspondence-type/member-correspondence-type.state";
import { ProfessionalDomicileAddress } from "~app/addresses/store/member-professional-domicile/member-professional-domicile.model";
import { MemberProfessionalDomicileState } from "~app/addresses/store/member-professional-domicile/member-professional-domicile.state";
import { MemberResidenceState } from "~app/addresses/store/member-residence/member-residence.state";
import { MemberResidence } from "~app/addresses/store/member-residence/models/member-residence.model";
import { MemberSecondaryWorkAddressState } from "~app/addresses/store/member-secondary-work-address/member-secondary-work-address.state";
import { MemberSecondaryWorkAddress } from "~app/addresses/store/member-secondary-work-address/models/member-secondary-work-address.model";
import { MemberRegistration } from "../year-selection/member-registration.model";
import { YearSelectionOdataQueryService } from "../year-selection/year-selection-odata-query.service";
import { AnnualRegistration } from "../year-selection/year-selection.model";
import { InscriptionCommandService } from "./inscription-command.service";
import { InscriptionFirebaseService } from "./inscription-firebase.service";
import { InscriptionQueryService } from "./inscription-query.service";
import {
  DownloadPdf,
  GeneratePDFImages,
  GeneratePDFImagesFailure,
  GeneratePDFImagesSuccess,
  GeneratePDFReceiptImage,
  GeneratePDFReceiptImageFailure,
  GeneratePDFReceiptImageSuccess,
  InitInscriptionForm,
  InitInscriptionFormSuccess,
  LoadInscriptionForm,
  LoadInscriptionFormFailure,
  LoadInscriptionFormSuccess,
  LoadInscriptionQuestions,
  LoadInscriptionQuestionsSuccess,
  LoadMemberEmail,
  LoadMemberEmailFailure,
  LoadMemberEmailSuccess,
  LoadMemberRegistrations,
  LoadMemberRegistrationsFailure,
  LoadMemberRegistrationsSuccess,
  LoadMemberSubscriptionFees,
  LoadMemberSubscriptionFeesFailure,
  LoadMemberSubscriptionFeesSuccess,
  LoadMemberWorkHours,
  LoadMemberWorkHoursSuccess,
  ProceedInscriptionChequePayment,
  ProceedInscriptionChequePaymentSuccess,
  ProceedInscriptionPayment,
  ProceedInscriptionPaymentFailure,
  ProceedInscriptionPaymentSuccess,
  SaveFormAnswers,
  SaveInscriptionForm,
  SelectYearForm,
  SetInscriptionAlreadyPayed,
  SubmitInscriptionForm,
  SubmitInscriptionFormFailure,
  SubmitInscriptionFormSuccess,
  UploadPdf,
  UploadPdfFailure,
  UploadPdfSuccess,
} from "./inscription.actions";
import {
  AnnualRegistrationForm,
  DynamicQuestion,
  PaymentReceipt,
  RegistrationFormState,
  SectionFormulaire,
  WorkHour,
} from "./inscription.model";
import { MemberBillQueryService } from "./member-bill-query.service";
import { MemberUnpaidBill } from "./member-bill.model";
import { MemberSubscriptionFeeQueryService } from "./member-subscriptionfee-query.service";
import { MemberSubscriptionFee } from "./member-subscriptionfeee.model";
import { ImageToAdd } from "./pdf-summary.model";
import { PdfSummaryService } from "./pdf-summary.service";

export class InscriptionStateModel {
  year: number;
  workHours: WorkHour[];
  inscriptionFormData: AnnualRegistrationForm;
  inscritpionQuestions: DynamicQuestion[];
  memberId: string;
  currentMemberEmail: string;
  paymentError: string;
  formClosureProcessing: boolean;
  processingInscription = false;
  submissionError: boolean = false;
  pdfImagesToAdd?: ImageToAdd[];
  pdfImagesLoading: boolean = false;
  paymentReceipt: PaymentReceipt;
  memberSubscriptionFees: MemberSubscriptionFee[];
  annualRegistrations: AnnualRegistration[];
  memberRegistrations: MemberRegistration[];
  inscriptionAlreadyPayed: boolean;
  workAddressesForUpdate: MemberSecondaryWorkAddress[];
}

@State<InscriptionStateModel>({
  name: "inscription",
  defaults: new InscriptionStateModel(),
})
@Injectable()
export class InscriptionState implements OnDestroy {
  private destroy$ = new Subject<void>();

  @Select(AuthState.user) user$: Observable<AuthUser>;

  constructor(
    private userMemberService: UserMemberService,
    private inscriptionQueryService: InscriptionQueryService,
    private memberSubscriptionFeeQueryService: MemberSubscriptionFeeQueryService,
    private odataQueryService: YearSelectionOdataQueryService,
    private commandService: InscriptionCommandService,
    private firebaseService: InscriptionFirebaseService,
    private memberBillService: MemberBillQueryService,
    private pdfSummaryService: PdfSummaryService,
    private translate: TranslateService,
    private router: Router,
    private store: Store
  ) { }

  @Selector()
  static year(state: InscriptionStateModel): number {
    return state.year;
  }

  @Selector()
  static workHours(state: InscriptionStateModel): WorkHour[] {
    return state.workHours;
  }

  @Selector()
  static voluntaryContribution(state: InscriptionStateModel): number {
    const value = state.inscriptionFormData?.answers?.find(
      (ans) => ans.questionNumber === 601
    )?.value;
    return !!value ? Number(value) : 0;
  }

  @Selector()
  static submissionError(state: InscriptionStateModel) {
    return state.submissionError;
  }

  @Selector()
  static paymentReceipt(state: InscriptionStateModel) {
    return state.inscriptionFormData?.paymentReceipt;
  }

  @Selector()
  static inscritpionQuestions(state: InscriptionStateModel) {
    return state.inscritpionQuestions;
  }

  @Selector()
  static inscriptionFormData(state: InscriptionStateModel) {
    return state.inscriptionFormData;
  }

  @Selector()
  static memberUnpaidBills(state: InscriptionStateModel): MemberUnpaidBill[] {
    return state.inscriptionFormData.memberUnpaidBills;
  }

  @Selector()
  static memberSubscriptionFees(
    state: InscriptionStateModel
  ): MemberSubscriptionFee[] {
    return state.memberSubscriptionFees;
  }

  @Selector()
  static annualRegistrations(
    state: InscriptionStateModel
  ): AnnualRegistration[] {
    return state.annualRegistrations || [];
  }

  @Selector()
  static memberRegistrations(
    state: InscriptionStateModel
  ): MemberRegistration[] {
    return state.memberRegistrations || [];
  }

  @Selector()
  static paymentError(state: InscriptionStateModel) {
    return state.paymentError;
  }
  @Selector()
  static pdfReady(state: InscriptionStateModel): boolean {
    return !!state.inscriptionFormData?.pdfDocumentId;
  }
  @Selector()
  static currentMemberId(state: InscriptionStateModel): string {
    return state.memberId;
  }

  @Selector()
  static currentMemberEmail(state: InscriptionStateModel): string {
    return state.currentMemberEmail;
  }

  @Selector()
  static pdfImagesLoading(state: InscriptionStateModel): boolean {
    return state.pdfImagesLoading;
  }

  @Selector()
  static inscriptionAlreadyPayed(state: InscriptionStateModel): boolean {
    return state.inscriptionAlreadyPayed;
  }

  @Selector()
  static processingInscription(state: InscriptionStateModel): boolean {
    return state?.processingInscription;
  }

  ngxsOnInit({ patchState, getState }: StateContext<InscriptionStateModel>) {
    this.odataQueryService
      .getAnnualRegistrations()
      .pipe(
        take(1),
        map((annualRegistrations) => {
          patchState({ annualRegistrations: annualRegistrations });
        })
      )
      .subscribe();
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Action(SelectYearForm)
  onSelectYearForm(
    { patchState, dispatch }: StateContext<InscriptionStateModel>,
    { year }: SelectYearForm
  ) {
    patchState({ year });

    this.userMemberService.memberId$
      .pipe(
        map((memberId) => {
          patchState({ memberId });
          dispatch(new LoadInscriptionForm());
          dispatch(new LoadMemberRegistrations(memberId));
          dispatch(new MemberResidenceActions.LoadMemberResidences(memberId));
          dispatch(
            new MemberCorrespondenceTypeActions.LoadMemberCorrespondenceTypes(
              memberId
            )
          );
          dispatch(
            new MemberProfessionalDomicileActions.LoadProfessionalDomiciles(
              memberId
            )
          );
          dispatch(
            new MemberSecondaryWorkAddressActions.LoadSecondaryWorkAddress(
              memberId
            )
          );
          dispatch(new LoadMemberWorkHours(memberId));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  @Action(InitInscriptionForm)
  onInitInscriptionForm(
    { getState, dispatch }: StateContext<InscriptionStateModel>,
    { }: InitInscriptionForm
  ) {
    return this.inscriptionQueryService
      .getPreviousYearAnswer(getState().memberId, getState().year)
      .pipe(
        map((answers) => {
          const formanswers = answers
            ?.filter((answer) => {
              const sections = [
                SectionFormulaire.ACTIVITE_PROF_ACTE_DOMAINE,
                SectionFormulaire.ACTIVITE_PROF_ACTE_CONDITION,
                SectionFormulaire.ACTIVITE_PROF_MODALITE_SEDATION,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPES_ACTES,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_SEDATION,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_REGIONAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_PROVINCIAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_EDUCATIONAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_PROFESSIONAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_EMAIL,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_3D,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_ORTHESE_MANDIBULAIRE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_IMPLANTO_PORTEE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_DOSSIER,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_RADIO,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_3D,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_AUTISME,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_BACKUP,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_BACKUP_STORAGE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_EMPREINTE_NUMERIQUE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_FACTURATION,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_FACTURATION_LOGICIEL,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_FACTURATION_LOGICIEL_SPECIFY,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_HEURES_TRAVAILLEE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_NOUVEAU_DOMAINE_PRATIQUE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_RADIOLOGIE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_RDV,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_RDV_LOGICIEL,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_RDV_LOGICIEL_SPECIFY,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_SEDATION,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_ANESTHESIE,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_DOSSIER,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_DOSSIER_LOGICIEL,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_DOSSIER_LOGICIEL_SPECIFY,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_RADIO,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_RADIO_LOGICIEL,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPE_RADIO_LOGICIEL_SPECIFY,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TYPES_ACTES,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TROUBLES_COGNITIFS_10_ANS_PLUS,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_TROUBLES_COGNITIFS_10_ANS_MOINS,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_PERTE_MOBILITE_10_ANS_PLUS,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_PERTE_MOBILITE_10_ANS_MOINS,
                SectionFormulaire.ACTIVITES_PROFESSIONNELLES_CONSENTEMENT_TROUVER_DENTISTE,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_EDUCATIONAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_EMAIL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_PROFESSIONAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_PROVINCIAL,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_PUBLIC,
                SectionFormulaire.ADRESSE_PROTECTION_RENSEIGNEMENTS__PERSONELS_REGIONAL,
                "pas necessaire",
              ];
              return sections.indexOf(answer.Section) >= 0;
            })
            .map((answer) => ({
              questionNumber: answer.QuestionNumber,
              value: answer.Value,
              year: getState().year,
            }));
          return {
            workHours: null,
            answers: formanswers || [],
            workAddressesForUpdate: null
          };
        }),
        switchMap((formData) =>
          this.firebaseService.updateCurrentInscription(
            getState().memberId,
            formData,
            getState().year
          )
        ),
        map((res) => dispatch(new InitInscriptionFormSuccess(res)))
      );
  }

  @Action(InitInscriptionFormSuccess)
  onInitInscriptionFormSuccess({
    dispatch,
  }: StateContext<InscriptionStateModel>) {
    dispatch(new LoadInscriptionForm());
  }

  @Action(LoadInscriptionForm)
  onLoadInscriptionForm(
    { getState, dispatch }: StateContext<InscriptionStateModel>,
    { }: LoadInscriptionForm
  ) {
    return this.firebaseService
      .getInscriptionFormDatas(getState().memberId, getState().year)
      .pipe(
        // If no collection found, initialize it.
        map((res) => {
          if (!!res) {
            dispatch(new LoadInscriptionFormSuccess(res));
          } else {
            dispatch(new InitInscriptionForm());
          }
        })
      );
  }

  @Action(LoadInscriptionFormSuccess)
  onLoadInscriptionFormSuccess(
    { patchState }: StateContext<InscriptionStateModel>,
    { data }: LoadInscriptionFormSuccess
  ) {
    patchState({ inscriptionFormData: data });
  }

  @Action(SaveFormAnswers)
  onSaveFormAnswers(
    { dispatch, getState }: StateContext<InscriptionStateModel>,
    { formData }: SaveFormAnswers
  ) {
    const formStateWithBills = {
      ...getState().inscriptionFormData,
      workHours: formData.workHours,
      answers: formData.answers,
      workAddressesForUpdate: formData.workAddressesForUpdate
    };

    dispatch(
      new SaveInscriptionForm(formStateWithBills, RegistrationFormState.DRAFT)
    );
  }

  @Action(SaveInscriptionForm)
  onSaveInscriptionForm(
    { patchState, getState }: StateContext<InscriptionStateModel>,
    { inscription, state, redirectToPaymentReceipt }: SaveInscriptionForm
  ) {
    const formData = { ...inscription, state };
    patchState({ inscriptionFormData: formData });
    this.firebaseService
      .updateCurrentInscription(getState().memberId, formData, getState().year)
      .then(() => {
        if (redirectToPaymentReceipt) {
          patchState({ processingInscription: false });
          this.router.navigate(["inscription", getState().year, "receipt"]);
        }
      });
  }

  @Action(LoadInscriptionQuestions)
  onLoadInscriptionQuestions(
    { dispatch, patchState }: StateContext<InscriptionStateModel>,
    { year }: LoadInscriptionQuestions
  ) {
    return this.inscriptionQueryService.getInscriptionQuestions(year).pipe(
      map((res) => {
        patchState({ year });
        dispatch(new LoadInscriptionQuestionsSuccess(res));
      }),
      catchError((err) => dispatch(new LoadInscriptionFormFailure(err)))
    );
  }

  @Action(LoadInscriptionQuestionsSuccess)
  onLoadInscriptionQuestionsSuccess(
    { patchState }: StateContext<InscriptionStateModel>,
    { data }: LoadInscriptionQuestionsSuccess
  ) {
    patchState({ inscritpionQuestions: data });
  }

  @Action(LoadMemberEmail)
  onLoadMemberEmail(
    { getState, dispatch }: StateContext<InscriptionStateModel>,
    { }: LoadMemberEmail
  ) {
    return this.inscriptionQueryService
      .getMemberEmail(getState().memberId)
      .pipe(
        tap((res) => dispatch(new LoadMemberEmailSuccess(res))),
        catchError((err) => dispatch(new LoadMemberEmailFailure(err)))
      );
  }

  @Action(LoadMemberEmailSuccess)
  onLoadMemberEmailSuccess(
    { patchState }: StateContext<InscriptionStateModel>,
    { email }: LoadMemberEmailSuccess
  ) {
    patchState({ currentMemberEmail: email });
  }

  @Action(ProceedInscriptionPayment)
  ProceedInscriptionPayment(
    { dispatch, getState, patchState }: StateContext<InscriptionStateModel>,
    { dto }: ProceedInscriptionPayment
  ) {
    patchState({ paymentError: "", processingInscription: true });
    return this.commandService
      .proceedInscriptionPayment(getState().memberId, dto)
      .pipe(
        map((data) => dispatch(new ProceedInscriptionPaymentSuccess(data))),
        catchError((err) => dispatch(new ProceedInscriptionPaymentFailure(err)))
      );
  }

  @Action(ProceedInscriptionPaymentSuccess)
  ProceedInscriptionPaymentSuccess(
    { dispatch, patchState, getState }: StateContext<InscriptionStateModel>,
    { data }: ProceedInscriptionPaymentSuccess
  ) {
    const formStateWithReceipt = {
      ...getState().inscriptionFormData,
      paymentReceipt: data,
    };
    patchState({
      formClosureProcessing: true,
      processingInscription: true,
      inscriptionFormData: formStateWithReceipt,
    });
    // Submit form to Back-end
    dispatch(new SubmitInscriptionForm());
  }

  @Action(ProceedInscriptionPaymentFailure)
  ProceedInscriptionPaymentFailure(
    { patchState }: StateContext<InscriptionStateModel>,
    { error }: ProceedInscriptionPaymentFailure
  ) {
    patchState({ formClosureProcessing: false, processingInscription: false });
    const errorStatus: string = error?.error?.message;
    let message = "";
    if (error.status == 404) {
      // No bills found
      message = this.translate.instant("inscription.form.payment.noBillsFound");
    } else if (error.status == 400) {
      // Moneris error or wrong amount
      message = this.translate.instant(
        "inscription.form.payment.sumOrMonerisError"
      );
    } else if (
      !!errorStatus &&
      errorStatus.toUpperCase().indexOf("DECLINED") >= 0
    ) {
      message = this.translate.instant(
        "inscription.form.payment.transactionError"
      );
    } else {
      // Todo : Transaction refusée si message DECLINED ou responseCode error
      // Other error
      message = this.translate.instant("inscription.form.payment.paymentError");
    }
    patchState({ paymentError: message });
  }

  @Action(SubmitInscriptionForm)
  onSubmitInscriptionForm({
    getState,
    dispatch,
    patchState
  }: StateContext<InscriptionStateModel>) {
    var residence = this.store.selectSnapshot<MemberResidence>(
      MemberResidenceState.currentResidence
    );
    var professionalDomicile =
      this.store.selectSnapshot<ProfessionalDomicileAddress>(
        MemberProfessionalDomicileState.currentProfessionalDomicile
      );
    var correspondenceType =
      this.store.selectSnapshot<MemberCorrespondenceType>(
        MemberCorrespondenceTypeState.currentCorrespondenceType
      );
    var workAddresses = this.store.selectSnapshot<MemberSecondaryWorkAddress[]>(
      MemberSecondaryWorkAddressState.workAddresses
    );

    // It's only for displaying invoices in the pdf
    // the calculation for the invoices is done backend 
    var unpaidBills = getState().memberSubscriptionFees;

    let formData: AnnualRegistrationForm = {
      ...getState().inscriptionFormData,
    };

    let memberUnpaidBills: MemberUnpaidBill[] = [];

    unpaidBills.map(ub => {
      memberUnpaidBills.push({
        accountingBatchId: '',
        amount: ub.amount ?? 0,
        billDate: new Date(),
        feeTypeCode: ub.code,
        feeTypeId: '',
        feeTypeLabel: ub.feeTypeLabel,
        memberBillCode: '',
        memberBillId: ''
      })
    });

    formData.memberUnpaidBills = memberUnpaidBills;

    patchState({ inscriptionFormData: formData });

    return this.commandService
      .submitInscriptionForm(
        getState().memberId,
        formData,
        getState().year.toString(),
        getState().inscritpionQuestions,
        residence,
        professionalDomicile,
        correspondenceType,
        workAddresses
      )
      .pipe(
        map((res) => dispatch(new SubmitInscriptionFormSuccess(res))),
        catchError((err) => dispatch(new SubmitInscriptionFormFailure(err)))
      );
  }

  @Action(SubmitInscriptionFormSuccess)
  onSubmitInscriptionFormSuccess({
    patchState,
    getState,
    dispatch,
  }: StateContext<InscriptionStateModel>) {
    patchState({ formClosureProcessing: false, submissionError: false });
    dispatch(
      new SaveInscriptionForm(
        getState().inscriptionFormData,
        RegistrationFormState.SUBMITTED,
        true
      )
    );
  }

  @Action(SubmitInscriptionFormFailure)
  onSubmitInscriptionFormFailure({
    patchState,
    dispatch,
    getState,
  }: StateContext<InscriptionStateModel>) {
    patchState({ formClosureProcessing: false, submissionError: true });
    dispatch(
      new SaveInscriptionForm(
        getState().inscriptionFormData,
        RegistrationFormState.ERROR,
        true
      )
    );
  }

  @Action(GeneratePDFImages)
  onGeneratePDF({ dispatch, patchState }: StateContext<InscriptionStateModel>) {
    patchState({ pdfImagesLoading: true });
    return this.pdfSummaryService.generateImagesForPDF().pipe(
      map((data) => dispatch(new GeneratePDFImagesSuccess(data))),
      catchError((err) => dispatch(new GeneratePDFImagesFailure(err)))
    );
  }

  @Action(GeneratePDFImagesSuccess)
  onGeneratePDFSuccess(
    { patchState }: StateContext<InscriptionStateModel>,
    { images }: GeneratePDFImagesSuccess
  ) {
    patchState({ pdfImagesToAdd: images, pdfImagesLoading: false });
  }

  @Action(GeneratePDFImagesFailure)
  onGeneratePDFFailure(
    { patchState }: StateContext<InscriptionStateModel>,
    { error }: GeneratePDFImagesFailure
  ) {
    patchState({ pdfImagesToAdd: [], pdfImagesLoading: false });
  }

  @Action(GeneratePDFReceiptImage)
  onGeneratePDFReceiptImage({
    dispatch,
    getState,
  }: StateContext<InscriptionStateModel>) {
    //If not exist already
    if (!getState().inscriptionFormData?.pdfDocumentId) {
      return this.pdfSummaryService.generatePDFReceiptImage().pipe(
        map((data) => dispatch(new GeneratePDFReceiptImageSuccess(data))),
        catchError((err) => dispatch(new GeneratePDFReceiptImageFailure(err)))
      );
    }
  }

  @Action(GeneratePDFReceiptImageSuccess)
  onGeneratePDFReceiptImageSuccess(
    { getState, patchState, dispatch }: StateContext<InscriptionStateModel>,
    { image }: GeneratePDFReceiptImageSuccess
  ) {
    patchState({ pdfImagesToAdd: [image, ...getState()?.pdfImagesToAdd] });

    dispatch(new UploadPdf());
  }

  @Action(UploadPdf)
  onAddMemberDocument({
    dispatch,
    getState,
  }: StateContext<InscriptionStateModel>) {
    const tilte = `Registration-${getState().year}.pdf`;
    const pdfBlob = this.pdfSummaryService.generatePDF(
      getState()?.pdfImagesToAdd
    );
    const document = {
      documentTypeId: "b0c21fb7-2093-5fec-9bee-c78da74f4186", // TODO : make Dynamic
      note: `Registration-${getState().year}.pdf`,
      file: new File([pdfBlob], tilte),
      uploadedByUserId: getState().memberId,
    };

    return this.commandService
      .createMemberDocument(getState().memberId, document)
      .pipe(
        tap((documentId) => dispatch(new UploadPdfSuccess(documentId))),
        catchError((err) => dispatch(new UploadPdfFailure(err)))
      );
  }

  @Action(UploadPdfSuccess)
  onUploadPdfSuccess(
    { getState, dispatch, patchState }: StateContext<InscriptionStateModel>,
    { documentId }: UploadPdfSuccess
  ) {
    const formStateWithBills = {
      ...getState().inscriptionFormData,
      pdfDocumentId: documentId,
    };
    dispatch(
      new SaveInscriptionForm(
        formStateWithBills,
        RegistrationFormState.SUBMITTED
      )
    );
  }

  @Action(DownloadPdf)
  onDownloadPdf({ getState }: StateContext<InscriptionStateModel>) {
    return this.commandService.authorizeDocumentDownload(
      getState().memberId,
      getState().inscriptionFormData?.pdfDocumentId,
      `Résumé inscription ${getState().year}.pdf`
    );
  }

  @Action(ProceedInscriptionChequePayment)
  onProceedInscriptionChequePayment(
    { getState, patchState, dispatch }: StateContext<InscriptionStateModel>,
    { data }: ProceedInscriptionChequePayment
  ) {
    const formStateWithReceipt = {
      ...getState().inscriptionFormData,
      paymentReceipt: data,
    };
    patchState({
      formClosureProcessing: true,
      processingInscription: true,
      inscriptionFormData: formStateWithReceipt,
    });
    const dto = {
      amount: data.amount,
      year: data.year,
      voluntaryContribution: data.voluntaryContribution,
      formId: data.memberId,
      payByCheque: data.payByCheque,
      isResubscription: data.isReSubscription,
      validFromDate: data.validFromDate,
      validToDate: data.validToDate,
      isAnnualCampaign: data.isAnnualCampaign,
    };
    return this.commandService
      .proceedInscriptionChequePayment(getState().memberId, dto)
      .pipe(
        map((data) =>
          dispatch(new ProceedInscriptionChequePaymentSuccess(data))
        ),
        catchError((err) => dispatch(new ProceedInscriptionPaymentFailure(err)))
      );
  }

  @Action(ProceedInscriptionChequePaymentSuccess)
  onProceedInscriptionChequePaymentSuccess(
    { patchState, dispatch }: StateContext<InscriptionStateModel>,
    { data }: ProceedInscriptionChequePaymentSuccess
  ) {
    console.log("ProceedInscriptionChequePaymentSuccess", data);
    patchState({ formClosureProcessing: true, processingInscription: true });
    // Submit form to Back-end
    dispatch(new SubmitInscriptionForm());
  }

  @Action(LoadMemberWorkHours)
  onLoadMemberWorkHours(
    { dispatch }: StateContext<InscriptionStateModel>,
    { memberId }: LoadMemberWorkHours
  ) {
    return this.inscriptionQueryService.getMemberWorkHours(memberId).pipe(
      map((res) => {
        dispatch(new LoadMemberWorkHoursSuccess(res));
      })
    );
  }

  @Action(LoadMemberWorkHoursSuccess)
  onLoadMemberWorkHoursSuccess(
    { patchState }: StateContext<InscriptionStateModel>,
    { workHours }: LoadMemberWorkHoursSuccess
  ) {
    patchState({ workHours: workHours });
  }

  @Action(LoadMemberSubscriptionFees)
  onLoadMemberSubscriptionFees(
    { dispatch, getState }: StateContext<InscriptionStateModel>,
    { memberId, isResubscription, donationAmount }: LoadMemberSubscriptionFees
  ) {
    const year = getState().year ? getState().year : 2024;
    const currentAnnualRegistration = getState()
      .annualRegistrations.filter((ar) => ar.year == year)
      .pop();

    let validFrom = currentAnnualRegistration.startDate;
    let validTo = currentAnnualRegistration.endDate;

    let filteredMemberRegistrations = getState().memberRegistrations.filter(
      (mr) => Number.parseInt(mr.period.substring(0, 4)) == getState().year
    );

    if (filteredMemberRegistrations.length > 0) {
      let memberRegistration = filteredMemberRegistrations.pop();

      validFrom = memberRegistration.validFrom;
      validTo = memberRegistration.validTo;
    }

    return this.memberSubscriptionFeeQueryService
      .getMemberSubscriptionFees(
        memberId,
        isResubscription,
        year,
        validFrom,
        validTo,
        donationAmount
      )
      .pipe(
        map(fees => fees.sort((a, b) => {
          if (a.isUnpaidBillType === b.isUnpaidBillType) {
            return 0;
          }
          return a.isUnpaidBillType ? 1 : -1;

        })),
        map((res) => {
          dispatch(new LoadMemberSubscriptionFeesSuccess(res));
        }),
        catchError((err) =>
          dispatch(new LoadMemberSubscriptionFeesFailure(err))
        )
      );
  }

  @Action(LoadMemberSubscriptionFeesSuccess)
  onLoadMemberSubscriptionFeesSuccess(
    { patchState, getState }: StateContext<InscriptionStateModel>,
    { memberSubscriptionFees }: LoadMemberSubscriptionFeesSuccess
  ) {
    patchState({ memberSubscriptionFees: memberSubscriptionFees });
  }

  @Action(LoadMemberRegistrations)
  onLoadMemberRegistrations(
    { dispatch }: StateContext<InscriptionStateModel>,
    { memberId }: LoadMemberRegistrations
  ) {
    return this.odataQueryService.loadMemberRegistrations(memberId).pipe(
      map((res) => {
        dispatch(new LoadMemberRegistrationsSuccess(res)),
          catchError((err) =>
            dispatch(new LoadMemberRegistrationsFailure(err))
          );
      })
    );
  }

  @Action(LoadMemberRegistrationsSuccess)
  onLoadMemeberRegistrationsSuccess(
    { patchState }: StateContext<InscriptionStateModel>,
    { memberRegistrations }: LoadMemberRegistrationsSuccess
  ) {
    patchState({ memberRegistrations: memberRegistrations });
  }

  @Action(SetInscriptionAlreadyPayed)
  onSetInscriptionAlreadyPayed(
    { patchState }: StateContext<InscriptionStateModel>,
    { value }: SetInscriptionAlreadyPayed
  ) {
    patchState({ inscriptionAlreadyPayed: value });
  }
}
